import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { authApi, type AuthRequestOptions } from '@/api/auth'
import type { Campus, LoginParams, Organization, SmsLoginParams, User } from '@/types'
import {
  AUTH_SESSION_KEY,
  type AuthSessionSnapshot,
  parseAuthSessionJson,
  readAuthSession,
  restoreAuthSession,
  writeAuthSession
} from '@/utils/authSessionStorage'
import { logger } from '@/utils/logger'
import { useAccountsStore } from './accounts'
import { resetAllTenantScopedStores } from './_tenantScopedRegistry'

const SILENT_AUTH_REQUEST_OPTIONS: AuthRequestOptions = { skipUnauthorizedHandler: true }
const PROACTIVE_REFRESH_MARGIN_MS = 5 * 60 * 1000
/**
 * 跨标签页刷新互斥锁名（Web Locks API），同源全部标签页在此串行。
 * 事故 2026-08-30：多标签页在同一时刻各自发起主动刷新，把同一个一次性
 * refresh token（同一 jti）重复提交，后端判定重放并吊销整会话，用户被强制登出。
 */
const REFRESH_LOCK_NAME = 'smart-campus-auth-refresh'
/** 采纳其它标签页轮换出的新令牌时，要求新 access token 至少还有这么长有效期。 */
const ADOPT_MIN_VALID_MS = 60 * 1000

type ActiveSession = {
  accessToken: string
  refreshToken: string
  user: User
}

function decodeJwtExp(token: string): number | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    while (payload.length % 4) payload += '='
    const json = JSON.parse(atob(payload))
    return typeof json.exp === 'number' ? json.exp * 1000 : null
  } catch {
    return null
  }
}

/** 读取 token 的 role claim（后端签发时已大写规范化），解析失败返回 null */
function decodeJwtRoleClaim(token: string): string | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    while (payload.length % 4) payload += '='
    const json = JSON.parse(atob(payload))
    return typeof json.role === 'string' ? json.role : null
  } catch {
    return null
  }
}

function hasUsableAccessToken(accessToken: string | null): boolean {
  if (!accessToken) return false
  const expiresAt = decodeJwtExp(accessToken)
  if (!expiresAt) return false
  return expiresAt > Date.now() + ADOPT_MIN_VALID_MS
}

/**
 * 用 Web Locks API 为刷新临界区加跨标签页互斥锁。
 * 排队的标签页拿到锁后会先检查存储中的令牌是否已被其它标签页轮换，
 * 从而保证同一个一次性 refresh token 只会被提交一次。
 * 不支持 Web Locks 的环境直接执行（退化为原有的标签页内单飞防护）。
 */
async function withCrossTabRefreshLock(criticalSection: () => Promise<boolean>): Promise<boolean> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (!locks) {
    return criticalSection()
  }
  let outcome = false
  await locks.request(REFRESH_LOCK_NAME, async () => {
    outcome = await criticalSection()
  })
  return outcome
}

type StorageSyncHandler = (event: StorageEvent) => void

let storageSyncHandler: StorageSyncHandler | null = null
let storageListenerBound = false

function bindCrossTabStorageListener(): void {
  if (storageListenerBound || typeof window === 'undefined') {
    return
  }
  storageListenerBound = true
  window.addEventListener('storage', (event: StorageEvent) => {
    storageSyncHandler?.(event)
  })
}

export const useUserStore = defineStore('user', () => {
  const token = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const userInfo = ref<User | null>(null)
  const currentOrg = ref<Organization | null>(null)
  const currentCampus = ref<Campus | null>(null)
  const hasHydrated = ref(false)
  const isHydrating = ref(false)
  let hydratePromise: Promise<boolean> | null = null
  let refreshPromise: Promise<boolean> | null = null
  let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null

  const isLoggedIn = computed(() => Boolean(token.value && userInfo.value))

  /** 当前激活角色编码（角色切换后即切换到的角色；未切换时等于主角色） */
  const activeRoleCode = computed(
    () => userInfo.value?.activeRoleCode ?? userInfo.value?.primaryRoleCode ?? userInfo.value?.role ?? null
  )

  /**
   * 校级管理角色集合：与后端 WuyuRoleGuard 的管理角色判定同口径
   * （admin/school_admin/tenant_admin/super_admin/platform_admin）。
   */
  const SCHOOL_ADMIN_ROLE_CODES = new Set(['admin', 'school_admin', 'tenant_admin', 'super_admin', 'platform_admin'])

  /**
   * 校级管理身份：多角色中任一命中即算（与后端多角色鉴权一致，不随激活角色变化）。
   * 供能力型 UI（五育勋章管理、洞察全校视角等）与后端鉴权保持同一判据。
   */
  const isSchoolAdmin = computed(() => {
    const user = userInfo.value
    if (!user) return false
    const codes = [user.role, user.primaryRoleCode, user.activeRoleCode, ...(user.roles ?? []).map((ref) => ref.code)]
    return codes.some((code) => code != null && SCHOOL_ADMIN_ROLE_CODES.has(code.toLowerCase()))
  })

  function persistSession(): void {
    if (token.value && refreshToken.value) {
      writeAuthSession({
        accessToken: token.value,
        refreshToken: refreshToken.value,
        user: userInfo.value
      })
      return
    }
    writeAuthSession(null)
  }

  function restorePersistedSession(): void {
    const snapshot = restoreAuthSession()
    token.value = snapshot?.accessToken ?? null
    refreshToken.value = snapshot?.refreshToken ?? null
    userInfo.value = snapshot?.user ?? null
  }

  function clearProactiveRefreshTimer(): void {
    if (proactiveRefreshTimer !== null) {
      clearTimeout(proactiveRefreshTimer)
      proactiveRefreshTimer = null
    }
  }

  function scheduleProactiveRefresh(): void {
    clearProactiveRefreshTimer()

    if (!token.value) return

    const expiresAt = decodeJwtExp(token.value)
    if (!expiresAt) return

    const now = Date.now()
    const refreshAt = expiresAt - PROACTIVE_REFRESH_MARGIN_MS
    const delay = Math.max(refreshAt - now, 0)

    proactiveRefreshTimer = setTimeout(async () => {
      try {
        const success = await refreshAccessToken(SILENT_AUTH_REQUEST_OPTIONS)
        if (success) {
          scheduleProactiveRefresh()
        }
      } catch {
        logger.debug('[User Store] 主动刷新失败，等待被动刷新兜底')
      }
    }, delay)
  }

  function activateSession(session: ActiveSession): void {
    token.value = session.accessToken
    refreshToken.value = session.refreshToken
    userInfo.value = session.user
    persistSession()
    scheduleProactiveRefresh()
  }

  function clearSession(): void {
    token.value = null
    refreshToken.value = null
    userInfo.value = null
    currentOrg.value = null
    currentCampus.value = null
    clearProactiveRefreshTimer()
    persistSession()
    resetBusinessStores()
  }

  async function fetchCurrentUser(options?: AuthRequestOptions): Promise<User> {
    const response = await authApi.getCurrentUser(options)
    userInfo.value = response.data
    persistSession()
    return response.data
  }

  /**
   * 切换当前激活角色：以目标角色重签访问令牌并清空业务缓存
   * （缓存数据属于切换前的身份视图）。refreshToken 与会话保持不变。
   *
   * 顺序约束：先清空业务缓存、再写入新会话。若先写会话（userInfo 变化会立刻
   * 触发页面级 watcher 重新拉取工作台等数据），后执行的清空会把这些在途请求
   * 作废，导致画布停留在默认模板——表现为"切角色后保存的布局丢失"。
   */
  async function switchRole(roleCode: string): Promise<User> {
    const response = await authApi.switchRole(roleCode, SILENT_AUTH_REQUEST_OPTIONS)
    const { accessToken, user } = response.data
    await resetBusinessStores()
    activateSession({
      accessToken,
      refreshToken: refreshToken.value ?? '',
      user
    })
    const accountsStore = useAccountsStore()
    accountsStore.updateActiveTokens(accessToken, refreshToken.value ?? '')
    return user
  }

  async function refreshAccessToken(_options?: AuthRequestOptions): Promise<boolean> {
    if (!refreshToken.value) {
      return false
    }

    if (refreshPromise) {
      return refreshPromise
    }

    const currentRefreshToken = refreshToken.value

    refreshPromise = (async () => {
      try {
        return await withCrossTabRefreshLock(async () => {
          const stored = readAuthSession()
          if (stored && stored.refreshToken !== currentRefreshToken) {
            if (hasUsableAccessToken(stored.accessToken)) {
              applyExternalSession(stored)
              return true
            }
            refreshToken.value = stored.refreshToken
          }

          if (!refreshToken.value) {
            return false
          }

          const response = await authApi.refreshToken(refreshToken.value, {
            skipUnauthorizedHandler: true
          })
          token.value = response.data.accessToken
          refreshToken.value = response.data.refreshToken
          persistSession()

          const accountsStore = useAccountsStore()
          accountsStore.updateActiveTokens(response.data.accessToken, response.data.refreshToken)

          // 身份收敛：续签后的 token 角色是后端权威激活角色（本地角色可能已被收回并回落主角色），
          // 与本地不一致时重拉 /me 修正，导航随即按真实身份收敛
          const tokenRole = decodeJwtRoleClaim(response.data.accessToken)
          const localRole =
            userInfo.value?.activeRoleCode ?? userInfo.value?.primaryRoleCode ?? userInfo.value?.role
          if (tokenRole && localRole && localRole.toUpperCase() !== tokenRole) {
            try {
              await fetchCurrentUser(SILENT_AUTH_REQUEST_OPTIONS)
            } catch {
              logger.debug('[User Store] 身份收敛失败，保留本地身份等待下次续签')
            }
          }

          scheduleProactiveRefresh()

          return true
        })
      } catch {
        return false
      } finally {
        refreshPromise = null
      }
    })()

    return refreshPromise
  }

  function applyExternalSession(snapshot: AuthSessionSnapshot): void {
    token.value = snapshot.accessToken
    refreshToken.value = snapshot.refreshToken
    if (snapshot.user) {
      userInfo.value = snapshot.user
    }
    const accountsStore = useAccountsStore()
    accountsStore.updateActiveTokens(snapshot.accessToken, snapshot.refreshToken)
    scheduleProactiveRefresh()
  }

  function snapshotFromStorageEvent(event: StorageEvent): AuthSessionSnapshot | 'cleared' | 'ignore' {
    if (event.key !== AUTH_SESSION_KEY && event.key !== null) {
      return 'ignore'
    }
    if (event.key === null || event.newValue === null) {
      return 'cleared'
    }
    if (event.newValue === undefined) {
      return readAuthSession() ?? 'cleared'
    }
    return parseAuthSessionJson(event.newValue) ?? 'cleared'
  }

  function onStorageEvent(event: StorageEvent): void {
    const snapshot = snapshotFromStorageEvent(event)
    if (snapshot === 'ignore') {
      return
    }
    if (snapshot === 'cleared') {
      if (!token.value && !refreshToken.value) {
        return
      }
      clearSession()
      hasHydrated.value = true
      return
    }
    if (snapshot.accessToken === token.value && snapshot.refreshToken === refreshToken.value) {
      if (snapshot.user) {
        userInfo.value = snapshot.user
      }
      return
    }
    applyExternalSession(snapshot)
  }

  function startCrossTabTokenSync(): void {
    storageSyncHandler = onStorageEvent
    bindCrossTabStorageListener()
  }

  async function login(params: LoginParams): Promise<User> {
    const session = (await authApi.login(params, SILENT_AUTH_REQUEST_OPTIONS)).data
    activateSession(session)
    hasHydrated.value = true

    const accountsStore = useAccountsStore()
    accountsStore.addAccount(session.user, session.accessToken, session.refreshToken)

    return session.user
  }

  /** 手机验证码登录：后端按手机号全局唯一定位账号，无需选择学校 */
  async function loginBySms(params: SmsLoginParams): Promise<User> {
    const session = (await authApi.loginBySms(params, SILENT_AUTH_REQUEST_OPTIONS)).data
    activateSession(session)
    hasHydrated.value = true

    const accountsStore = useAccountsStore()
    accountsStore.addAccount(session.user, session.accessToken, session.refreshToken)

    return session.user
  }

  async function validatePersistedSession(): Promise<boolean> {
    if (!token.value || !refreshToken.value) {
      clearSession()
      return false
    }

    try {
      await fetchCurrentUser(SILENT_AUTH_REQUEST_OPTIONS)
      return true
    } catch {
      try {
        const refreshed = await refreshAccessToken(SILENT_AUTH_REQUEST_OPTIONS)
        if (!refreshed) {
          clearSession()
          return false
        }

        await fetchCurrentUser(SILENT_AUTH_REQUEST_OPTIONS)
        return true
      } catch (error) {
        logger.warn('[User Store] Token 刷新失败:', error)
        clearSession()
        return false
      }
    }
  }

  async function hydrateSession(): Promise<boolean> {
    if (hasHydrated.value) {
      return isLoggedIn.value
    }

    if (hydratePromise) {
      return hydratePromise
    }

    hydratePromise = (async () => {
      isHydrating.value = true
      restorePersistedSession()
      startCrossTabTokenSync()

      try {
        const valid = await validatePersistedSession()
        if (valid && token.value && refreshToken.value && userInfo.value) {
          const accountsStore = useAccountsStore()
          accountsStore.addAccount(userInfo.value, token.value, refreshToken.value)
          scheduleProactiveRefresh()
        }
        return valid
      } finally {
        isHydrating.value = false
        hasHydrated.value = true
      }
    })()

    try {
      return await hydratePromise
    } finally {
      hydratePromise = null
    }
  }

  function setCurrentOrg(org: Organization): void {
    const changed = currentOrg.value?.id !== org.id
    currentOrg.value = org
    if (changed) {
      resetBusinessStores()
    }
  }

  function setCurrentCampus(campus: Campus): void {
    currentCampus.value = campus
  }

  async function resetBusinessStores(): Promise<void> {
    await resetAllTenantScopedStores()
  }

  async function logoutWithServer(): Promise<void> {
    try {
      if (token.value) {
        await authApi.logout(SILENT_AUTH_REQUEST_OPTIONS)
      }
    } catch (error) {
      logger.warn('[User Store] 服务端登出失败:', error)
    } finally {
      const accountsStore = useAccountsStore()
      if (userInfo.value) {
        const accountId = `${userInfo.value.tenantId ?? 0}:${userInfo.value.id}`
        accountsStore.removeAccount(accountId)
      }
      clearSession()
      hasHydrated.value = true
    }
  }

  function logout(): void {
    clearSession()
    hasHydrated.value = true
  }

  return {
    token,
    refreshToken,
    userInfo,
    currentOrg,
    currentCampus,
    hasHydrated,
    isHydrating,
    isLoggedIn,
    activeRoleCode,
    isSchoolAdmin,
    login,
    loginBySms,
    hydrateSession,
    fetchCurrentUser,
    switchRole,
    refreshAccessToken,
    activateSession,
    logoutWithServer,
    setCurrentOrg,
    setCurrentCampus,
    logout,
    resetBusinessStores
  }
})
